import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, finalize, of, tap } from 'rxjs';

/**
 * Kept free of `libs/core` and of anything heavy: the interceptor below is registered in
 * `app.config.ts`, so this file is part of the eager bundle.
 */

/** What the case view lends Pre-Check for the page on screen. */
export interface PreCheckPageHandle {
  /** Saves the page on screen without leaving it; emits once, whether it saved. */
  save(): Observable<boolean>;
  /** Re-reads the page on screen from its GET endpoint(s). */
  reload(): void;
}

interface WriteSession {
  started: number;
  finished: number;
  failed: boolean;
  lastActivity: number;
}

/** A write is quiet this long before the save is taken to be over. Long enough for a page
 *  that saves in two steps (save, then a follow-up request) to start its second one. */
const SETTLE_MS = 400;
const POLL_MS = 100;
/** A save that hasn't finished by now is treated as failed rather than waited on forever. */
const TIMEOUT_MS = 60_000;

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
/** Pre-Check's own requests are never part of a page's save. */
const PRE_CHECK_URL = '/case-review/pre-check';

/**
 * Saves the case page on screen before an issue on it is marked as addressed, and re-reads
 * it afterwards. The server records the flagged field's value at Mark as Addressed, so the
 * adviser's correction has to be saved first.
 *
 * The case pages have no common save API — each saves its own way, then moves on (the next
 * section, client or tab). So the page's normal save is run with the move "held": every place
 * a page moves on after a successful save first calls `holdsAdvance()`, which, during a held
 * save, reports the save as done and stops the move. Whether the save got that far is what
 * says it worked; the write requests it sent say when it is over and whether any failed.
 */
@Injectable({ providedIn: 'root' })
export class PreCheckPageSaveService {
  private handle: PreCheckPageHandle | null = null;
  private hold: (() => void) | null = null;
  private readonly sessions = new Set<WriteSession>();

  /** The case view lends its page; the returned function takes it back. */
  register(handle: PreCheckPageHandle): () => void {
    this.handle = handle;
    return () => {
      if (this.handle === handle) this.handle = null;
    };
  }

  /** Saves the page on screen. True when there is nothing to save. */
  saveActivePage(): Observable<boolean> {
    return this.handle?.save() ?? of(true);
  }

  reloadActivePage(): void {
    this.handle?.reload();
  }

  /**
   * For every place a page moves on after a successful save: true when the save was run for
   * Pre-Check, in which case it has been reported as done and the page must stay where it is.
   */
  holdsAdvance(): boolean {
    const done = this.hold;
    if (!done) return false;
    this.hold = null;
    done();
    return true;
  }

  /**
   * Runs a page's save with its move held, and emits whether it saved: true once it reached
   * its move-on point and its writes all succeeded; false when a write failed, or when it went
   * quiet without getting there — stopped by validation (the page shows why) or by an error.
   */
  runHeld(start: () => void): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      const session: WriteSession = { started: 0, finished: 0, failed: false, lastActivity: Date.now() };
      const startedAt = Date.now();
      let reached = false;
      let timer: ReturnType<typeof setInterval> | null = null;

      const cleanUp = () => {
        if (timer !== null) clearInterval(timer);
        timer = null;
        this.sessions.delete(session);
        // A save that never got there must not hold the next real Next click.
        if (!reached) this.hold = null;
      };
      const finish = (saved: boolean) => {
        cleanUp();
        subscriber.next(saved);
        subscriber.complete();
      };

      this.sessions.add(session);
      this.hold = () => (reached = true);
      try {
        start();
      } catch {
        finish(false);
        return cleanUp;
      }

      timer = setInterval(() => {
        const quiet =
          session.started === session.finished && Date.now() - session.lastActivity >= SETTLE_MS;
        if (quiet) {
          finish(reached && !session.failed);
        } else if (Date.now() - startedAt > TIMEOUT_MS) {
          finish(false);
        }
      }, POLL_MS);

      return cleanUp;
    });
  }

  /** For the interceptor: a write request is starting. Null when no held save is running. */
  beginWrite(): { fail: () => void; end: () => void } | null {
    if (this.sessions.size === 0) return null;

    const sessions = [...this.sessions];
    const touch = () => sessions.forEach((session) => (session.lastActivity = Date.now()));
    sessions.forEach((session) => session.started++);
    touch();

    return {
      fail: () => sessions.forEach((session) => (session.failed = true)),
      end: () => {
        sessions.forEach((session) => session.finished++);
        touch();
      },
    };
  }
}

/** Tells `PreCheckPageSaveService` about the write requests a held page save sends. */
export const preCheckPageSaveInterceptor: HttpInterceptorFn = (req, next) => {
  if (READ_METHODS.has(req.method) || req.url.includes(PRE_CHECK_URL)) return next(req);

  const write = inject(PreCheckPageSaveService).beginWrite();
  if (!write) return next(req);

  return next(req).pipe(
    tap({
      next: (event) => {
        // Some endpoints answer 200 with `success: false`.
        if (event instanceof HttpResponse && (event.body as { success?: unknown } | null)?.success === false) {
          write.fail();
        }
      },
      error: () => write.fail(),
    }),
    finalize(() => write.end()),
  );
};
